import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { api, getApiEndpointName } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import EmailLogEntrySchema, {
  type EmailLogEntryJSON,
  type EmailLogEntrySummaryJSON,
  type EmailLogOutcome,
} from "users/super-users/emails/_schemas/email-log-entry-schema";
import EmailLogEntry, { EmailLogEntrySummary } from "users/super-users/emails/email-log-entry";
import z from "zod";

const EMAILS_URL = "super-user/emails";

/**
 * One page of the email log, newest first. Every filter is optional. `recordedFrom` is inclusive and
 * `recordedBefore` exclusive, both as instants.
 */
export type EmailLogQuery = {
  pagination: PaginationParameters;
  recipient?: string;
  template?: string;
  outcome?: EmailLogOutcome;
  recordedFrom?: Date;
  recordedBefore?: Date;
};

export class EmailLogAPIError extends Error {
  constructor(method: string | undefined, public readonly error: unknown) {
    const endpointName = method ? `.${method}` : "";
    super(`The email log request${endpointName} could not be completed.`);
  }
}

/** The super user Emails tab's reads (#6922). */
export const emailLogApi = api.injectEndpoints({
  endpoints: (build) => ({
    searchEmailLog: build.query<PaginatedResponse<EmailLogEntrySummary>, EmailLogQuery>({
      query: (request) => ({ url: EMAILS_URL, params: emailLogParams(request) }),
      rawResponseSchema: z.array(EmailLogEntrySchema.summary),
      onSchemaFailure: logSchemaFailure,
      // The page is the body; every total is in X-Pagination.
      transformResponse: (response: EmailLogEntrySummaryJSON[], meta) =>
        PaginatedResponse.from(
          response.map((row) => EmailLogEntrySummary.create(row)),
          meta?.response?.headers.get("x-pagination") ?? "",
        ),
      // A 403 stays a refusal and a 404 an absence: the view tells them apart by status.
      transformErrorResponse: toEmailLogApiError,
    }),
    // The id as a string, so the argument is equal from one render to the next.
    getEmailLogEntry: build.query<EmailLogEntry, string>({
      query: (id) => `${EMAILS_URL}/${encodeURIComponent(id)}`,
      rawResponseSchema: EmailLogEntrySchema.detail,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: EmailLogEntryJSON) => EmailLogEntry.create(response),
      transformErrorResponse: toEmailLogApiError,
      // Each read is recorded in the log's view trail, so a closed and reopened entry is read again.
      keepUnusedDataFor: 0,
    }),
  }),
});

export const { useSearchEmailLogQuery, useGetEmailLogEntryQuery } = emailLogApi;

function toEmailLogApiError(response: FetchBaseQueryError, meta?: unknown): EmailLogAPIError {
  return new EmailLogAPIError(getApiEndpointName(meta), response);
}

function emailLogParams(request: EmailLogQuery): URLSearchParams {
  const params = request.pagination.searchParams;
  const recipient = request.recipient?.trim();
  if (recipient) params.set("recipient", recipient);
  const template = request.template?.trim();
  if (template) params.set("template", template);
  if (request.outcome) params.set("outcome", request.outcome);
  if (request.recordedFrom) params.set("from", request.recordedFrom.toISOString());
  if (request.recordedBefore) params.set("to", request.recordedBefore.toISOString());
  return params;
}
